import { defineStore } from "pinia";
import { computed, onUnmounted, reactive } from "vue";
import { v4 as uuid } from "uuid";

export type ToastId = number | string;
export interface AppToast {
  id: ToastId;
  message: string;
  type: "success" | "error" | "info";
  persistent: boolean;
  title?: string;
  icon?: string;
  position?: "default" | "chat";
  closable?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}
export type AppToastType = AppToast["type"];

type ToastPayload = PartialExcept<AppToast, "message">;

const defaultToast: AppToast = {
  id: 0,
  message: "",
  persistent: false,
  type: "success",
};

const DEFAULT_TOAST_DURATION = 3000;
const MAX_TOASTS = 20;

export const useToastStore = defineStore("toasts", () => {
  const toasts = reactive<Map<ToastId, AppToast>>(new Map());
  const chatToasts = reactive<Map<ToastId, AppToast>>(new Map());

  function removeToast(id: ToastId) {
    toasts.delete(id);
    chatToasts.delete(id);
  }

  function _evictOldestIfOver(map: Map<ToastId, AppToast>) {
    if (map.size >= MAX_TOASTS) {
      map.delete(map.keys().next().value!);
    }
  }

  function addToast(payload: ToastPayload, duration = DEFAULT_TOAST_DURATION) {
    const toast = {
      ...defaultToast,
      ...payload,
      type: payload.type ?? "success",
      id: payload.id ?? uuid(),
      position: payload.position ?? "default",
    };

    if (payload.position === "chat") {
      _evictOldestIfOver(chatToasts);
      chatToasts.set(toast.id, toast);
    } else {
      _evictOldestIfOver(toasts);
      toasts.set(toast.id, toast);
    }

    if (!toast.persistent) {
      setTimeout(() => {
        removeToast(toast.id);
      }, duration);
    }

    return toast;
  }

  return {
    toasts,
    chatToasts,
    addToast,
    removeToast,
  };
});

/**
 * Use this composable to handle an individual loading process with a toast.
 *
 * You can call `set` to add a toast with a loading spinner and a message,
 * and keep calling `set` to change the message (and title) until the
 * loading process is done. Then call `finished` to remove the toast.
 *
 * @example
 * const asyncProcessToast = useLoadingToast();
 *
 * asyncProcessToast.set({
 *   message: "Async process in progress...",
 * });
 *
 * // Call the API
 * await startAsyncProcess();
 *
 * asyncProcessToast.finished({
 *   message: "Data loaded!",
 * });
 */
export function useSingleToast() {
  const toastId = uuid();
  const { toasts, addToast, removeToast } = useToastStore();

  const existingToast = computed(() => toasts.get(toastId));

  function set(
    payload: Pick<
      ToastPayload,
      "message" | "title" | "type" | "icon" | "action"
    >,
    persistent = false,
  ) {
    if (existingToast.value) {
      removeToast(toastId);
    }

    addToast({
      ...payload,
      id: toastId,
      persistent,
    });
  }

  function finished(
    payload: Pick<
      ToastPayload,
      "message" | "title" | "type" | "icon" | "action"
    >,
  ) {
    if (!existingToast.value) return;

    addToast({
      ...existingToast,
      ...payload,
      // This will remove the toast after the duration if it was persistent
      persistent: false,
      id: toastId,
    });
  }

  onUnmounted(() => {
    removeToast(toastId);
  });

  return {
    set,
    finished,
  };
}
